import { create } from 'zustand';
import type { TourStep } from './steps';

// Guided clickthrough overlay (Celonis-style). The overlay drives navigation
// itself, so this store tracks position plus which step source is running:
// the built-in product demo, or a PAI Academy recipe loaded from the academy
// service (whose proof-gated steps advance by verified backend state, not by
// clicking Next), or a step-by-step guide the Assistant wrote for a "how do I"
// question. Recipes and guides share the step list and its label.
interface TourStore {
  active: boolean;
  stepIndex: number;
  /** 'demo' renders steps.ts; 'academy' and 'guide' render academySteps. */
  source: 'demo' | 'academy' | 'guide';
  academySteps: TourStep[];
  academyLabel: string;
  enrollmentId: string | null;
  /** Step ids already proofed (hydrated from the backend, grows on pass). */
  proofedSteps: Set<string>;
  start: () => void;
  startAcademy: (opts: {
    steps: TourStep[];
    enrollmentId: string;
    label: string;
    proofed: string[];
    startIndex?: number;
  }) => void;
  /** Run an Assistant guide: no enrollment, nothing proof-gated. */
  startGuide: (opts: { steps: TourStep[]; label: string }) => void;
  markProofed: (stepId: string) => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
}

export const useTourStore = create<TourStore>((set) => ({
  active: false,
  stepIndex: 0,
  source: 'demo',
  academySteps: [],
  academyLabel: '',
  enrollmentId: null,
  proofedSteps: new Set<string>(),
  start: () => set({ active: true, stepIndex: 0, source: 'demo' }),
  startAcademy: ({ steps, enrollmentId, label, proofed, startIndex }) =>
    set({
      active: true,
      source: 'academy',
      academySteps: steps,
      academyLabel: label,
      enrollmentId,
      proofedSteps: new Set(proofed),
      stepIndex: Math.min(startIndex ?? 0, Math.max(0, steps.length - 1)),
    }),
  startGuide: ({ steps, label }) =>
    set({
      active: steps.length > 0,
      source: 'guide',
      academySteps: steps,
      academyLabel: label,
      enrollmentId: null,
      proofedSteps: new Set<string>(),
      stepIndex: 0,
    }),
  markProofed: (stepId) =>
    set((s) => {
      const next = new Set(s.proofedSteps);
      next.add(stepId);
      return { proofedSteps: next };
    }),
  stop: () => set({ active: false, stepIndex: 0 }),
  next: () => set((s) => ({ stepIndex: s.stepIndex + 1 })),
  prev: () => set((s) => ({ stepIndex: Math.max(0, s.stepIndex - 1) })),
}));
